import React, { useMemo } from 'react';

interface GuestStatsProps {
    guests: any[];
}

export const GuestStats: React.FC<GuestStatsProps> = ({ guests }) => {
    const stats = useMemo(() => {
        const initial = {
            totalGuests: 0,
            ie: { yes: 0, no: 0, pending: 0, total_people: 0 },
            es: { yes: 0, no: 0, pending: 0, total_people: 0 },
            pb: { yes: 0, no: 0, pending: 0, total_people: 0 },
            plusOnes: 0,
            children: 0
        };

        return guests.reduce((acc, g) => {
            acc.totalGuests++;

            const hasPlusOne = !!g.plusOneName;
            if (hasPlusOne) acc.plusOnes++;

            const kidsCount = typeof g.children === 'number' ? g.children : 0;
            acc.children += kidsCount;

            const extraPeopleCount = (hasPlusOne ? 1 : 0) + kidsCount;

            // Ireland
            const statusIE = g.status?.ie;
            if (statusIE === 'yes') {
                acc.ie.yes++;
                acc.ie.total_people += (1 + extraPeopleCount);
            } else if (statusIE === 'no') {
                acc.ie.no++;
            } else {
                acc.ie.pending++;
            }

            // Spain
            const statusES = g.status?.es;
            if (statusES === 'yes') {
                acc.es.yes++;
                acc.es.total_people += (1 + extraPeopleCount);
            } else if (statusES === 'no') {
                acc.es.no++;
            } else {
                acc.es.pending++;
            }

            // Pre-Boda (PB)
            const statusPB = g.status?.pb;
            if (statusPB === 'yes') {
                acc.pb.yes++;
                acc.pb.total_people += (1 + extraPeopleCount);
            } else if (statusPB === 'no') {
                acc.pb.no++;
            } else {
                acc.pb.pending++;
            }

            return acc;
        }, initial);
    }, [guests]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 font-sans">
            {/* Overview Card */}
            <div className="bg-white p-6 rounded-xl shadow-card border border-gray-100 flex flex-col justify-between">
                <div>
                    <h4 className="text-base font-bold text-secondary-light uppercase tracking-widest mb-4 border-b border-gray-50 pb-2">Overview</h4>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-secondary-light text-base">Total Invites</span>
                            <span className="font-bold text-secondary">{stats.totalGuests}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-secondary-light text-base">Total Plus Ones</span>
                            <span className="font-bold text-secondary">{stats.plusOnes}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-secondary-light text-base">Total Children Tracker</span>
                            <span className="font-bold text-secondary">{stats.children}</span>
                        </div>
                    </div>
                </div>
                <div className="flex justify-between border-t border-gray-100 pt-4 mt-6">
                    <span className="text-secondary font-bold text-base">Potential Headcount</span>
                    <span className="font-bold text-primary text-lg">{stats.totalGuests + stats.plusOnes + stats.children}</span>
                </div>
            </div>

            {/* Ireland Stats */}
            <div className="bg-white p-6 rounded-xl shadow-card border border-gray-100">
                <h4 className="text-base font-bold text-green-800 uppercase tracking-widest mb-4 border-b border-green-50 pb-2">
                    Ireland Celebration
                </h4>
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <span className="text-secondary-light text-base">Attending</span>
                        <span className="bg-green-50 text-green-700 py-1 px-3 rounded-full text-[10px] font-bold border border-green-100">{stats.ie.yes}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-secondary-light text-base">Declined</span>
                        <span className="bg-red-50 text-red-600 py-1 px-3 rounded-full text-[10px] font-bold border border-red-100">{stats.ie.no}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-secondary-light text-base">Pending</span>
                        <span className="bg-yellow-50 text-yellow-600 py-1 px-3 rounded-full text-[10px] font-bold border border-yellow-100">{stats.ie.pending}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-100 pt-4 mt-4">
                        <span className="text-secondary font-bold text-base">Total Confirmed People</span>
                        <span className="font-bold text-green-700 text-xl">{stats.ie.total_people}</span>
                    </div>
                </div>
            </div>

            {/* Spain Stats */}
            <div className="bg-white p-6 rounded-xl shadow-card border border-gray-100">
                <h4 className="text-base font-bold text-primary-dark uppercase tracking-widest mb-4 border-b border-primary-light/20 pb-2">
                    Spain Celebration
                </h4>
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <span className="text-secondary-light text-base">Attending</span>
                        <span className="bg-green-50 text-green-700 py-1 px-3 rounded-full text-[10px] font-bold border border-green-100">{stats.es.yes}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-secondary-light text-base">Declined</span>
                        <span className="bg-red-50 text-red-600 py-1 px-3 rounded-full text-[10px] font-bold border border-red-100">{stats.es.no}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-secondary-light text-base">Pending</span>
                        <span className="bg-yellow-50 text-yellow-600 py-1 px-3 rounded-full text-[10px] font-bold border border-yellow-100">{stats.es.pending}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-100 pt-4 mt-4">
                        <span className="text-secondary font-bold text-base">Total Confirmed People</span>
                        <span className="font-bold text-primary-dark text-xl">{stats.es.total_people}</span>
                    </div>
                </div>
            </div>

            {/* Pre-Boda Stats (Spans col depending on sizing, maybe grid-cols-4 or span below) */}
            <div className="bg-white p-6 rounded-xl shadow-card border border-gray-100">
                <h4 className="text-base font-bold text-blue-800 uppercase tracking-widest mb-4 border-b border-blue-50 pb-2">
                    Pre-Boda Celebration
                </h4>
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <span className="text-secondary-light text-base">Attending</span>
                        <span className="bg-green-50 text-green-700 py-1 px-3 rounded-full text-[10px] font-bold border border-green-100">{stats.pb.yes}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-secondary-light text-base">Declined</span>
                        <span className="bg-red-50 text-red-600 py-1 px-3 rounded-full text-[10px] font-bold border border-red-100">{stats.pb.no}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-secondary-light text-base">Pending</span>
                        <span className="bg-yellow-50 text-yellow-600 py-1 px-3 rounded-full text-[10px] font-bold border border-yellow-100">{stats.pb.pending}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-100 pt-4 mt-4">
                        <span className="text-secondary font-bold text-base">Total Confirmed People</span>
                        <span className="font-bold text-blue-700 text-xl">{stats.pb.total_people}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
